// useTrialExpiration.js
import { useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const useTrialExpiration = () => {
  const checkIntervalRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  const checkTrialExpiration = async () => {
    if (!currentUser) return;

    // Don't check trial expiration if user is currently in a paid signup flow
    const currentPath = window.location.pathname;
    if (currentPath.includes('/paid-signup') || currentPath.includes('/subscription')) {

      return;
    }

    // Check if there's an active upgrade process
    const upgradeInProgress = sessionStorage.getItem('upgradeInProgress');
    if (upgradeInProgress === 'true') {

      return;
    }

    try {
      // Get user's subscription info from backend
      const response = await fetch(`/api/subscription/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {

        return;
      }

      const subscriptionData = await response.json();
      
      // Check if user is on trial and if trial has expired
      if (subscriptionData.isTrialSubscription && subscriptionData.trialEndDate) {
        const now = new Date();
        const trialEnd = new Date(subscriptionData.trialEndDate);
        
        if (now >= trialEnd) {

          
          // Record the trial expiration
          try {
            await fetch(`/api/subscription/expire-trial`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${await currentUser.getIdToken()}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userId: currentUser.uid,
                email: currentUser.email || ''
              }),
            });
          } catch (error) {

          }

          // Set flag for cross-tab communication and store user info
          localStorage.setItem("trialExpired", "true");
          localStorage.setItem("expiredTrialUserId", currentUser.uid);
          
          // Navigate first, then logout
          navigate("/subscription-required", { 
            state: { trialExpired: true },
            replace: true
          });
          
          // Small delay to ensure navigation happens before logout
          setTimeout(async () => {
            try {
              await logout();
            } catch (error) {

            }
          }, 100);
        } else {
          const timeDiff = trialEnd - now;
          const minutesRemaining = Math.ceil(timeDiff / (1000 * 60));

        }
      }
    } catch (error) {

    }
  };

  useEffect(() => {
    if (!currentUser) return;

    // Check immediately on mount
    checkTrialExpiration();

    // Set up periodic checking every 24 hours
    checkIntervalRef.current = setInterval(checkTrialExpiration, 24 * 60 * 60 * 1000);

    // Listen for storage events (cross-tab communication)
    const handleStorageChange = (e) => {
      if (e.key === "trialExpired" && e.newValue === "true") {
        // Navigate first, then logout with delay
        navigate("/subscription-required", { 
          state: { trialExpired: true },
          replace: true
        });
        
        setTimeout(async () => {
          try {
            await logout();
          } catch (error) {

          }
        }, 100);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Cleanup on unmount
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [currentUser]);
};

export default useTrialExpiration;